import { ChangeEvent, memo, useCallback } from 'react';

import { TablePagination } from '@material-ui/core';
import clsx from 'clsx';

import { defaultPerPageOptions } from '../../hooks/useNewDataTable/NewDataTable.utils';
import { useStyles } from './TablePaginationBar.styles';
import { TablePaginationBarProps } from './TablePaginationBar.types';

/**
 * The data-table pagination footer (arrows, "Rows per page" select, "x–y of z"
 * range) as a standalone component, for paginated views that are not tables —
 * lists, card feeds. Same look as the footer `useOasisDataTable` renders.
 *
 * Callbacks speak plain numbers, and `page` is zero-based the way MUI counts —
 * a consumer fed by one-based API meta converts at the boundary.
 */
export const TablePaginationBar = memo(
  ({
    count,
    page,
    rowsPerPage,
    rowsPerPageOptions = defaultPerPageOptions,
    component = 'div',
    colSpan,
    loading,
    className,
    onPageChange,
    onRowsPerPageChange,
  }: TablePaginationBarProps) => {
    const styles = useStyles();

    const handlePageChange = useCallback((_: unknown, nextPage: number) => onPageChange(nextPage), [onPageChange]);

    const handleRowsPerPageChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onRowsPerPageChange(Number(event.target.value)),
      [onRowsPerPageChange],
    );

    return (
      <TablePagination
        classes={{
          root: clsx(styles.root, className),
          toolbar: styles.toolbar,
          spacer: styles.spacer,
          caption: styles.caption,
        }}
        component={component}
        colSpan={colSpan}
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        backIconButtonProps={loading ? { disabled: true } : undefined}
        nextIconButtonProps={loading ? { disabled: true } : undefined}
        SelectProps={loading ? { disabled: true } : undefined}
      />
    );
  },
);

TablePaginationBar.displayName = 'TablePaginationBar';

export type { TablePaginationBarProps } from './TablePaginationBar.types';
